/**
 * Corridor analysis for the selected route: what sits beside it, and where it
 * cannot be avoided. Opt-in, because the scan reads several large tables.
 */
import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Slider,
  TextField,
  Typography,
} from '@mui/material';
import {
  DistanceUnits,
  RiskDataset,
  RouteRiskReport,
  RouteRiskSegment,
  RouteRiskSettings,
} from '../types';
import { formatDistance } from '../geo/format';
import { RISK_FLOOR, riskColor, riskLabel } from '../map/risk';
import { IntelSection } from './intel';
import {
  DockAction,
  EmptyState,
  ErrorLine,
  GOLD,
  Loading,
  Provenance,
  TabHeader,
} from './DockPrimitives';

const DATASET_LABEL: Record<RiskDataset, string> = {
  chokepoints: 'Chokepoints',
  rf: 'RF alerts',
  dwells: 'Dwells',
  congestion: 'Traffic',
};

const LOOKBACK_CHOICES = [
  { value: 24, label: '24 hours' },
  { value: 168, label: '7 days' },
  { value: 720, label: '30 days' },
  { value: 2160, label: '90 days' },
];

function lookbackLabel(hours: number): string {
  const match = LOOKBACK_CHOICES.find((c) => c.value === hours);
  if (match) return match.label;
  return hours >= 48 ? `${Math.round(hours / 24)} days` : `${hours} hours`;
}

const Hotspot: React.FC<{
  segment: RouteRiskSegment;
  units: DistanceUnits;
  onFocus?: () => void;
}> = ({ segment, units, onFocus }) => (
  <Box
    onClick={onFocus}
    role={onFocus ? 'button' : undefined}
    tabIndex={onFocus ? 0 : -1}
    onKeyDown={(e) => { if (e.key === 'Enter') onFocus?.(); }}
    sx={{
      display: 'flex',
      gap: 0.75,
      py: 0.5,
      cursor: onFocus ? 'pointer' : 'default',
      '&:hover .risk-where': onFocus ? { textDecoration: 'underline' } : undefined,
      '&:focus-visible': { outline: `1px solid ${GOLD}`, outlineOffset: 2 },
    }}
  >
    <Box
      sx={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        mt: 0.6,
        flexShrink: 0,
        bgcolor: riskColor(segment.score),
      }}
    />
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
        <Typography className="risk-where" variant="caption" noWrap sx={{ fontWeight: 700, minWidth: 0 }}>
          {segment.label || `${riskLabel(segment.score)} risk`}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ flexShrink: 0 }}>
          {formatDistance(segment.start_distance, units)} in
        </Typography>
      </Box>
      {segment.findings.map((finding) => (
        <Typography
          key={finding}
          variant="caption"
          sx={{ display: 'block', fontSize: '0.66rem', color: 'text.secondary' }}
        >
          {finding}
        </Typography>
      ))}
    </Box>
  </Box>
);

export interface CorridorTabProps {
  section: IntelSection<RouteRiskReport>;
  units: DistanceUnits;
  hasRoute: boolean;
  settings?: RouteRiskSettings;
  onSettingsChange?: (next: Partial<RouteRiskSettings>) => void;
  onFocusSegment?: (segment: RouteRiskSegment) => void;
}

const CorridorTab: React.FC<CorridorTabProps> = ({
  section,
  units,
  hasRoute,
  settings,
  onSettingsChange,
  onFocusSegment,
}) => {
  const [settingsOpen, setSettingsOpen] = useState(false);

  const report = section.data;
  const analyze = () => {
    if (section.enabled) section.refresh?.();
    else section.setEnabled?.(true);
  };
  const canAnalyze = hasRoute && (!!section.setEnabled || !!section.refresh);

  const corridorM = settings?.corridorM ?? report?.corridor_m ?? 250;
  const lookbackHours = settings?.lookbackHours ?? report?.lookback_hours ?? 720;

  const unavailable = report
    ? (Object.entries(report.datasets) as Array<[RiskDataset, RouteRiskReport['datasets'][RiskDataset]]>)
      .filter(([, status]) => !status.available)
    : [];
  const chokepoints = report?.segments.filter((s) => s.chokepoint).length || 0;

  return (
    <Box>
      <TabHeader
        title="Corridor"
        hint={report ? `${corridorM} m · ${lookbackLabel(lookbackHours)}` : undefined}
        actions={(
          <>
            {onSettingsChange && (
              <DockAction onClick={() => setSettingsOpen(true)} title="Corridor width and lookback">
                Scope
              </DockAction>
            )}
            {canAnalyze && (
              <DockAction onClick={analyze} disabled={section.loading}>
                {report ? 'Re-analyze' : 'Analyze'}
              </DockAction>
            )}
          </>
        )}
      />

      {section.loading && <Loading label="Scanning the corridor…" />}
      {section.error && <ErrorLine>{section.error}</ErrorLine>}

      {!report && !section.loading && (
        <EmptyState>
          {hasRoute
            ? 'Scan for chokepoints, RF alerts and habitual dwells beside this route.'
            : 'Plan a route to analyse the corridor around it.'}
        </EmptyState>
      )}

      {report && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {chokepoints > 0
              ? `${chokepoints} unavoidable stretch${chokepoints === 1 ? '' : 'es'}`
              : 'No unavoidable stretches'}
            {report.datasets.rf.available && ` · ${report.datasets.rf.matched} RF alerts`}
            {report.datasets.dwells.available && ` · ${report.datasets.dwells.matched} dwells`}
          </Typography>

          {report.hotspots.length === 0 && (
            <EmptyState>
              Nothing above the reporting threshold within {corridorM} m of this route.
            </EmptyState>
          )}

          {report.hotspots.map((segment) => (
            <Hotspot
              key={segment.index}
              segment={segment}
              units={units}
              onFocus={onFocusSegment ? () => onFocusSegment(segment) : undefined}
            />
          ))}

          {unavailable.length > 0 && (
            <Typography
              variant="caption"
              sx={{ display: 'block', mt: 0.75, fontSize: '0.64rem', color: 'text.disabled' }}
            >
              Not counted — {unavailable.map(([key, status]) => (
                `${DATASET_LABEL[key]}: ${status.note || 'unavailable'}`
              )).join(' ')}
            </Typography>
          )}

          {(report.datasets.rf.truncated || report.datasets.dwells.truncated) && (
            <Typography
              variant="caption"
              sx={{ display: 'block', fontSize: '0.64rem', color: 'text.disabled' }}
            >
              Result limit reached — narrow the corridor or the lookback for a complete count.
            </Typography>
          )}
        </>
      )}

      <Provenance section={section} onRefresh={canAnalyze ? analyze : undefined} />

      <Dialog open={settingsOpen} onClose={() => setSettingsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>Corridor analysis</Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 1.5 }}>
            Corridor width — {corridorM} m either side
          </Typography>
          <Slider
            size="small"
            value={corridorM}
            min={50}
            max={1000}
            step={50}
            marks={[{ value: 50, label: '50 m' }, { value: 1000, label: '1 km' }]}
            valueLabelDisplay="auto"
            onChange={(_, value) => onSettingsChange?.({ corridorM: value as number })}
            sx={{ color: GOLD, mb: 1.5 }}
          />
          <TextField
            select
            size="small"
            fullWidth
            variant="standard"
            label="Look back"
            value={lookbackHours}
            onChange={(e) => onSettingsChange?.({ lookbackHours: Number(e.target.value) })}
          >
            {LOOKBACK_CHOICES.map((choice) => (
              <MenuItem key={choice.value} value={choice.value}>{choice.label}</MenuItem>
            ))}
          </TextField>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Segments scoring below {RISK_FLOOR} are treated as noise and left off the list.
          </Typography>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CorridorTab;
